import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { UserSettings, ConnectCredentialsRequest, ConnectCredentialsResponse, SaveSettingsRequest, WatchList, Chart } from '../models/settings.model';

@Injectable({
  providedIn: 'root'
})
export class SettingsService {
  private settingsSubject = new BehaviorSubject<UserSettings | null>(null);
  public settings$ = this.settingsSubject.asObservable();

  private watchListsSubject = new BehaviorSubject<WatchList[]>([]);
  public watchLists$ = this.watchListsSubject.asObservable();

  private chartsSubject = new BehaviorSubject<Chart[]>([]);
  public charts$ = this.chartsSubject.asObservable();

  constructor(private http: HttpClient) {}

  public get currentSettings(): UserSettings | null {
    return this.settingsSubject.value;
  }

  public get currentWatchLists(): WatchList[] {
    return this.watchListsSubject.value;
  }

  public get currentCharts(): Chart[] {
    return this.chartsSubject.value;
  }

  getSettings(): Observable<UserSettings> {
    return this.http.get<UserSettings>(`${environment.apiUrl}/settings`).pipe(
      tap(settings => {
        // Normalize IDs to numbers to handle potential string/number mismatch from API
        const normalizedSettings: UserSettings = {
          ...settings,
          selectedWatchListId: settings.selectedWatchListId != null ? Number(settings.selectedWatchListId) : null,
          selectedChartId: settings.selectedChartId != null ? Number(settings.selectedChartId) : null,
          watchLists: settings.watchLists?.map(w => ({ ...w, id: Number(w.id) })),
          charts: settings.charts?.map(c => ({ ...c, id: Number(c.id) })),
          selectedWatchListTickers: settings.selectedWatchListTickers ?? null
        };
        this.settingsSubject.next(normalizedSettings);
        if (normalizedSettings.watchLists) {
          this.watchListsSubject.next(normalizedSettings.watchLists);
        }
        if (normalizedSettings.charts) {
          this.chartsSubject.next(normalizedSettings.charts);
        }
      })
    );
  }

  connectTradingView(credentials: ConnectCredentialsRequest): Observable<ConnectCredentialsResponse> {
    return this.http.post<ConnectCredentialsResponse>(`${environment.apiUrl}/settings/connect`, credentials).pipe(
      tap(response => {
        // Normalize IDs to numbers
        const normalizedWatchLists = response.watchLists?.map(w => ({ ...w, id: Number(w.id) })) || [];
        const normalizedCharts = response.charts?.map(c => ({ ...c, id: Number(c.id) })) || [];
        
        const current = this.settingsSubject.value;
        if (current) {
          this.settingsSubject.next({ 
            ...current, 
            userCredentialsSet: true,
            watchLists: normalizedWatchLists,
            charts: normalizedCharts
          });
        }
        this.watchListsSubject.next(normalizedWatchLists);
        this.chartsSubject.next(normalizedCharts);
      })
    );
  }

  disconnectTradingView(): Observable<any> {
    return this.http.post(`${environment.apiUrl}/settings/disconnect`, {}).pipe(
      tap(() => {
        const current = this.settingsSubject.value;
        if (current) {
          this.settingsSubject.next({ 
            ...current, 
            userCredentialsSet: false,
            selectedWatchListId: null,
            selectedWatchListName: null,
            selectedChartId: null,
            selectedChartName: null,
            watchLists: [],
            charts: []
          });
        }
        this.watchListsSubject.next([]);
        this.chartsSubject.next([]);
      })
    );
  }

  saveSettings(request: SaveSettingsRequest): Observable<any> {
    return this.http.post(`${environment.apiUrl}/settings/save`, request).pipe(
      tap(() => {
        const current = this.settingsSubject.value;
        const watchLists = this.watchListsSubject.value;
        const charts = this.chartsSubject.value;

        const selectedWatchList = watchLists.find(w => w.id === request.watchListId);
        const selectedChart = charts.find(c => c.id === request.chartId);

        // Determine selectedWatchListTickers based on request
        let selectedWatchListTickers: string[] | null = null;
        if (request.symbols && Array.isArray(request.symbols)) {
          selectedWatchListTickers = request.symbols;
        }
        // If always fetch is enabled, symbols is undefined, so set to null
        if (current) {
          this.settingsSubject.next({
            ...current,
            selectedWatchListId: request.watchListId,
            selectedWatchListName: selectedWatchList?.name || null,
            selectedChartId: request.chartId,
            selectedChartName: selectedChart?.name || null,
            selectedWatchListTickers: selectedWatchListTickers && selectedWatchListTickers.length > 0 ? selectedWatchListTickers : null
          });
        }
      })
    );
  }

  refreshTradingView(): Observable<ConnectCredentialsResponse> {
    return this.http.post<ConnectCredentialsResponse>(`${environment.apiUrl}/settings/refresh`, {}).pipe(
      tap(response => {
        // Normalize IDs to numbers
        const normalizedWatchLists = response.watchLists?.map(w => ({ ...w, id: Number(w.id) })) || [];
        const normalizedCharts = response.charts?.map(c => ({ ...c, id: Number(c.id) })) || [];
        
        const current = this.settingsSubject.value;
        if (current) {
          this.settingsSubject.next({ 
            ...current, 
            watchLists: normalizedWatchLists,
            charts: normalizedCharts,
            selectedWatchListId: null,
            selectedWatchListName: null,
            selectedChartId: null,
            selectedChartName: null
          });
        }
        this.watchListsSubject.next(normalizedWatchLists);
        this.chartsSubject.next(normalizedCharts);
      })
    );
  }

  updateSettings(settings: UserSettings): void {
    this.settingsSubject.next(settings);
  }
}
